import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { useTenant } from './TenantContext.js';
import { authApi } from '../services/authApi.js';
import { getAuthHeadersSync } from '../utils/authHelpers';
import { useAuth } from '../../context/AuthContext.js';

// Default session timeout values (in minutes)
const DEFAULT_IDLE_TIMEOUT = 30;
const DEFAULT_ABSOLUTE_TIMEOUT = 12 * 60; // 12 hours in minutes

interface SessionContextType {
  // Session state
  isAuthenticated: boolean;
  isSessionExpired: boolean;
  idleTimeoutMinutes: number;
  absoluteTimeoutMinutes: number;
  lastActivityTime: Date | null;
  sessionStartTime: Date | null;
  
  // Session actions
  refreshSession: () => void;
  logout: () => void;
  updateActivity: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant } = useTenant();
  const { isAuthenticated: authContextAuthenticated, logout: authContextLogout } = useAuth();
  
  // Session state - use AuthContext as single source of truth
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authContextAuthenticated);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState<number>(DEFAULT_IDLE_TIMEOUT);
  const [absoluteTimeoutMinutes, setAbsoluteTimeoutMinutes] = useState<number>(DEFAULT_ABSOLUTE_TIMEOUT);
  const [lastActivityTime, setLastActivityTime] = useState<Date | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  // Refs so the timeout-check interval can read current values without being in its dependency array.
  // Without refs, adding lastActivityTime/sessionStartTime to deps causes the interval to be
  // cleared and recreated on every user activity event (thrashing — interval never fires).
  const lastActivityTimeRef = useRef<Date | null>(null);
  const sessionStartTimeRef = useRef<Date | null>(null);
  
  // Initialize session on mount
  useEffect(() => {
    if (isAuthenticated) {
      // Set initial session times
      const now = new Date();
      setLastActivityTime(now);
      
      // If no session start time is stored, set it now
      const storedStartTime = localStorage.getItem('sessionStartTime');
      if (storedStartTime) {
        const parsedStartTime = new Date(storedStartTime);
        if (!isNaN(parsedStartTime.getTime())) {
          // If the stored session start time is already beyond the default absolute
          // timeout window, treat it as stale data from a previous session and reset
          // to the current time. This prevents an immediate "maximum session duration"
          // expiry right after a fresh login when old localStorage data is present.
          const minutesSinceStoredStart = (now.getTime() - parsedStartTime.getTime()) / (1000 * 60);
          if (minutesSinceStoredStart >= DEFAULT_ABSOLUTE_TIMEOUT) {
            setSessionStartTime(now);
            sessionStartTimeRef.current = now;
            localStorage.setItem('sessionStartTime', now.toISOString());
          } else {
            setSessionStartTime(parsedStartTime);
            sessionStartTimeRef.current = parsedStartTime;
          }
        } else {
          // Invalid date in storage, reset to now
          setSessionStartTime(now);
          sessionStartTimeRef.current = now;
          localStorage.setItem('sessionStartTime', now.toISOString());
        }
      } else {
        setSessionStartTime(now);
        sessionStartTimeRef.current = now;
        localStorage.setItem('sessionStartTime', now.toISOString());
      }
      
      // Load tenant-specific timeout settings
      fetchTenantTimeoutSettings();
    }
  }, [isAuthenticated, tenant]);
  
  // Fetch tenant-specific timeout settings from backend
  const fetchTenantTimeoutSettings = async () => {
    try {
      // Call the tenant settings API to get timeout values
      const response = await fetch(`${authApi.baseURL}/api/admin/platform-settings`, {
        headers: getAuthHeadersSync()
      });
      
      if (response.ok) {
        const settings = await response.json();
        if (settings.session_idle_timeout_minutes) {
          setIdleTimeoutMinutes(settings.session_idle_timeout_minutes);
        }
        if (settings.session_absolute_timeout_minutes) {
          setAbsoluteTimeoutMinutes(settings.session_absolute_timeout_minutes);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tenant timeout settings:', error);
      // Fall back to defaults if API call fails
    }
  };
  
  // Check for session timeouts with backend validation
  useEffect(() => {
    if (!isAuthenticated || isSessionExpired) return;
    
    const checkSessionTimeouts = async () => {
      const now = new Date();

      // Read current values from refs (not closure-captured state) to avoid stale reads.
      const currentSessionStart = sessionStartTimeRef.current;
      const currentLastActivity = lastActivityTimeRef.current;
      
      // CRITICAL FIX: Check absolute timeout FIRST (cannot be bypassed by activity)
      // This fixes the security bug where keeping tab active bypasses 12-hour timeout
      if (currentSessionStart) {
        const sessionDuration = (now.getTime() - currentSessionStart.getTime()) / (1000 * 60);
        if (sessionDuration >= absoluteTimeoutMinutes) {
          console.log('Session expired due to maximum session duration (12 hours)');
          setIsSessionExpired(true);
          authContextLogout(); // Use AuthContext logout for proper state sync
          return;
        }
      }
      
      // Check idle timeout (local check)
      if (currentLastActivity) {
        const idleTime = (now.getTime() - currentLastActivity.getTime()) / (1000 * 60);
        if (idleTime >= idleTimeoutMinutes) {
          console.log('Session expired due to inactivity (30 minutes)');
          setIsSessionExpired(true);
          authContextLogout(); // Use AuthContext logout for proper state sync
          return;
        }
      }
      
      // Backend session validation (optional - only if backend endpoint exists)
      // This validates against Redis session store
      // GRACEFUL DEGRADATION: If backend not ready, frontend timeout logic still works
      try {
        const response = await fetch(`${authApi.baseURL}/api/auth/validate-session`, {
          method: 'POST',
          headers: getAuthHeadersSync()
        });
        
        // Only logout on explicit 401 (session expired)
        if (response.status === 401) {
          console.log('Backend session validation: session expired (401)');
          setIsSessionExpired(true);
          authContextLogout();
          return;
        }
        
        // Ignore 404/405 (endpoint not implemented yet) - frontend logic still works
        if (response.status === 404 || response.status === 405) {
          console.debug('Backend session validation endpoint not available - using frontend-only validation');
          return;
        }
        
        // Other errors (500, etc.) - log but don't logout
        if (!response.ok) {
          console.warn(`Backend session validation returned ${response.status} - continuing with frontend validation`);
          return;
        }
      } catch (error) {
        // Network errors, CORS, etc. - don't logout, frontend validation continues
        console.debug('Backend session validation unavailable (network error) - using frontend-only validation:', error);
      }
    };
    
    // Check timeouts every minute
    const intervalId = window.setInterval(checkSessionTimeouts, 60 * 1000);
    
    // Initial check
    checkSessionTimeouts();
    
    return () => {
      window.clearInterval(intervalId);
    };
    // lastActivityTime and sessionStartTime are intentionally NOT in deps — they are read
    // via refs (lastActivityTimeRef / sessionStartTimeRef) to prevent interval thrashing.
    // Every activity update would otherwise destroy and recreate the 60s interval, meaning
    // it could never fire for active users.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSessionExpired, idleTimeoutMinutes, absoluteTimeoutMinutes]);
  
  // Track user activity
  useEffect(() => {
    if (!isAuthenticated || isSessionExpired) return;
    
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    
    const handleUserActivity = () => {
      updateActivity();
    };
    
    // Add event listeners for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSessionExpired]);
  
  // Sync with AuthContext authentication state
  useEffect(() => {
    setIsAuthenticated(authContextAuthenticated);
  }, [authContextAuthenticated]);
  
  // Update user activity timestamp and notify backend (if available)
  // SECURITY FIX: Debounced to prevent excessive API calls from user activity
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateActivity = useCallback(
    debounce(async () => {
      if (isAuthenticated && !isSessionExpired) {
        const now = new Date();
        setLastActivityTime(now);
        lastActivityTimeRef.current = now;
        
        // Backend activity tracking (optional - only if endpoint exists)
        // GRACEFUL DEGRADATION: If backend not ready, frontend tracking still works
        try {
          const response = await fetch(`${authApi.baseURL}/api/auth/activity`, {
            method: 'POST',
            headers: getAuthHeadersSync()
          });
          
          // Only logout on explicit 401 (session expired)
          if (response.status === 401) {
            console.log('Backend session expired during activity tracking');
            setIsSessionExpired(true);
            authContextLogout();
            return;
          }
          
          // Ignore 404/405 (endpoint not implemented yet) - frontend tracking still works
          if (response.status === 404 || response.status === 405) {
            // Silently ignore - backend not ready yet, frontend handles it
            return;
          }
          
          // Success or other non-critical errors
          if (response.ok) {
            console.debug('Activity tracked on backend');
          }
        } catch (error) {
          // Network errors, CORS, etc. - silently ignore, frontend tracking continues
          // Activity will be tracked on next successful API request
        }
      }
    }, 5000, { leading: true, trailing: false }), // Debounce for 5 seconds, execute on leading edge
    [isAuthenticated, isSessionExpired]
  );
  
  // Refresh the session
  const refreshSession = async () => {
    try {
      // Call refresh token endpoint if available
      const response = await fetch(`${authApi.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`,
          'X-Tenant-ID': tenant
        }
      });
      
      if (response.ok) {
        const { access_token } = await response.json();
        // Use correct token storage key to match AuthContext
        localStorage.setItem('astrapeak_auth_token', access_token);
        
        // Reset session state
        setIsSessionExpired(false);
        const now = new Date();
        setLastActivityTime(now);
        // NOTE: Do NOT reset sessionStartTime - absolute timeout must not be bypassed
        // Only idle timeout is reset when user continues session
      } else {
        // If refresh fails, logout
        logout();
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      logout();
    }
  };
  
  // Logout function
  const logout = () => {
    // Use AuthContext logout for proper state synchronization
    authContextLogout();
    
    // Clean up session-specific state
    setIsAuthenticated(false);
    setIsSessionExpired(true);
    setLastActivityTime(null);
    setSessionStartTime(null);
    localStorage.removeItem('sessionStartTime');
    
    // Redirect to login
    window.location.href = '/login';
  };
  
  return (
    <SessionContext.Provider
      value={{
        isAuthenticated,
        isSessionExpired,
        idleTimeoutMinutes,
        absoluteTimeoutMinutes,
        lastActivityTime,
        sessionStartTime,
        refreshSession,
        logout,
        updateActivity
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
